/**
 * Tier Configuration
 *
 * Defines subscription tier limits. Feature flag defaults are derived from
 * the Product Feature Catalog in @aica/core.
 *
 * ── Pricing SSOT ───────────────────────────────────────────────────────────
 * The RUNTIME source of truth for default rates (per-minute AND per-chat) is the
 * `tier_pricing` table in Postgres, edited via the admin panel (/admin/pricing)
 * and resolved at charge time by PricingPlanRepository.getEffectiveRate()
 * (org override in `pricing_plans` → tier default → free fallback). Customer
 * dashboards read the SAME effective rate through the billing-summary endpoints,
 * so the "price in settings" is a view of this SSOT, not a separate source.
 *
 * `overage_rate_cents` below is the CODE MIRROR of the per-minute ladder, used
 * for feature-flag/tier enforcement and Stripe product creation — it does NOT
 * drive per-minute billing. The website pricing page mirrors the same ladder for
 * display. When you change a rate, update all three (tier_pricing DB → here →
 * website); pricing-drift.test.ts fails if this file and the website diverge.
 * Per-chat rates live ONLY in `tier_pricing` (no code/website mirror).
 *
 * ── Base subscription price ─────────────────────────────────────────────────
 * `base_price_cents` is the monthly subscription price. Its SSOT is **Stripe**:
 * Stripe is what actually charges the card, so a number here that disagrees
 * with Stripe is a lie told to a customer, never a price they pay. This field
 * is therefore a MIRROR — the same relationship `overage_rate_cents` already
 * has to `tier_pricing`.
 *
 * It exists as data (not just prose) because the server had no base price at
 * all: it lived in Stripe, in the website's `calcTiers` array, and in a comment
 * here. Anything server-side that must NAME a price — the tier advisor, for
 * one — had to derive it from the ladder rate, and the ladder rate is rounded
 * up: 2500 × 5.2¢ = $130, while the plan costs $129. A dollar of drift in a
 * number quoted to a customer is not a rounding detail.
 *
 * Changing a price already requires a Stripe price + a new STRIPE_PRICE_* var
 * + a website edit + a deploy, so keeping the mirror here adds no new friction;
 * it just makes the coupling explicit and testable. pricing-drift.test.ts
 * asserts it against the website and against the ladder arithmetic.
 */

import { getFeatureFlagDefaultsForPackage } from '@aica/core/product-features';
import type { SubscriptionTier, TierLimitKey } from './types';

// ============================================================================
// Tier Limits Matrix
// ============================================================================

export const TIER_LIMITS: Record<SubscriptionTier, Record<TierLimitKey, number>> = {
  free: {
    // Pay-as-you-go: 100 trial minutes free, then overage drawn from the prepaid
    // wallet at 6.5¢/min (no hard stop — top up to keep going). See
    // docs/plans/2026-06-12-prepaid-billing-unification.md
    //
    // Raised from 30 on 2026-08-15: 30 minutes ran out before an org could form
    // a judgement about the product. Mirror of `subscription_tier_limits`
    // (migration 036) — the runtime reads the table, not this constant.
    included_minutes: 100,
    overage_rate_cents: 6.5,
    base_price_cents: 0, // no subscription — PAYG wallet only
    max_projects: 1,
    max_users: 2,
    // Every org is provisioned 14 default profiles (4 base + 10 bound),
    // so limits are 14 + allowed custom profiles. See
    // docs/plans/2026-06-10-category-prompt-profiles.md
    max_prompt_profiles: 16,
    max_alert_rules: 0,
    max_routing_rules: 1,
    // Custom call criteria (#857): the first count limit here that a route
    // actually enforces (POST /api/v1/call-criteria). Counts custom criteria
    // that are not archived, paused ones included; built-ins never count.
    // 5–20 across the ladder is what the #856 measurement confirmed (20
    // criteria: same quality, ~$0.0022/record). Enterprise gets 30, never -1:
    // an unlimited count would outgrow the strict response schema. The org
    // ceiling is this limit plus the built-in catalog (#868, spec §5).
    max_call_criteria: 5,
    can_topup_credits: 1, // PAYG overage requires wallet top-up
  },
  // Ladder rates are the derived price/minute and double as the overage rate
  // (same tier → no arbitrage): $59/1000=5.9¢, $129/2500=5.2¢, $269/6000=4.5¢.
  starter: {
    included_minutes: 1000,
    overage_rate_cents: 5.9,
    base_price_cents: 5900,
    max_projects: 3,
    max_users: 5,
    max_prompt_profiles: 20, // 14 defaults + 6 custom
    max_alert_rules: 3,
    max_routing_rules: 3,
    max_call_criteria: 5,
    can_topup_credits: 1,
  },
  pro: {
    included_minutes: 2500,
    overage_rate_cents: 5.2,
    base_price_cents: 12900,
    max_projects: 10,
    max_users: 15,
    max_prompt_profiles: 30, // 14 defaults + 16 custom
    max_alert_rules: 10,
    max_routing_rules: 10,
    max_call_criteria: 10,
    can_topup_credits: 1,
  },
  business: {
    included_minutes: 6000,
    overage_rate_cents: 4.5,
    base_price_cents: 26900,
    max_projects: -1, // unlimited
    max_users: 50,
    max_prompt_profiles: -1,
    max_alert_rules: -1,
    max_routing_rules: -1,
    max_call_criteria: 15,
    can_topup_credits: 1,
  },
  scale: {
    // $559/14000 = 4.0¢. Inherits all Business features; higher caps.
    included_minutes: 14000,
    overage_rate_cents: 4.0,
    base_price_cents: 55900,
    max_projects: -1, // unlimited
    max_users: 100,
    max_prompt_profiles: -1,
    max_alert_rules: -1,
    max_routing_rules: -1,
    max_call_criteria: 20,
    can_topup_credits: 1,
  },
  enterprise: {
    included_minutes: -1, // unlimited
    overage_rate_cents: 0,
    base_price_cents: -1, // negotiated per contract, not a published price
    max_projects: -1,
    max_users: -1,
    max_prompt_profiles: -1,
    max_alert_rules: -1,
    max_routing_rules: -1,
    max_call_criteria: 30,
    can_topup_credits: 0, // Custom billing
  },
};

// ============================================================================
// Tier Feature Flag Defaults
// ============================================================================

export const TIER_FEATURE_DEFAULTS: Record<SubscriptionTier, Record<string, boolean>> = {
  free: getFeatureFlagDefaultsForPackage('free'),
  starter: getFeatureFlagDefaultsForPackage('starter'),
  pro: getFeatureFlagDefaultsForPackage('pro'),
  business: getFeatureFlagDefaultsForPackage('business'),
  scale: getFeatureFlagDefaultsForPackage('scale'),
  enterprise: getFeatureFlagDefaultsForPackage('enterprise'),
};

// ============================================================================
// Tier Display Info
// ============================================================================

export const TIER_DISPLAY: Record<
  SubscriptionTier,
  { name: string; description: string }
> = {
  free: { name: 'Free', description: 'Get started with basic call analytics' },
  starter: { name: 'Starter', description: 'For small teams getting started' },
  pro: { name: 'Pro', description: 'For growing teams with advanced needs' },
  business: { name: 'Business', description: 'For large teams and enterprises' },
  scale: { name: 'Scale', description: 'For high-volume operations' },
  enterprise: {
    name: 'Enterprise',
    description: 'Custom solutions for your organization',
  },
};

// ============================================================================
// Helpers
// ============================================================================

/** Canonical low→high ordering of tiers (free cheapest, enterprise highest). */
const TIER_ORDER: SubscriptionTier[] = [
  'free',
  'starter',
  'pro',
  'business',
  'scale',
  'enterprise',
];

/**
 * Rank of a tier on the ladder (free = 0 … enterprise = 5). Use to tell an
 * upgrade (higher rank) from a downgrade (lower rank).
 */
export function getTierRank(tier: SubscriptionTier): number {
  return TIER_ORDER.indexOf(tier);
}

export function getTierLimit(tier: SubscriptionTier, key: TierLimitKey): number {
  return TIER_LIMITS[tier][key];
}

export function isUnlimited(value: number): boolean {
  return value === -1;
}

export function getTierFeatureDefaults(tier: SubscriptionTier): Record<string, boolean> {
  return { ...TIER_FEATURE_DEFAULTS[tier] };
}

/** Check if a tier upgrade is needed to access a limit */
export function isLimitExceeded(
  tier: SubscriptionTier,
  key: TierLimitKey,
  currentCount: number
): boolean {
  const limit = getTierLimit(tier, key);
  if (isUnlimited(limit)) return false;
  return currentCount >= limit;
}

/** Get the minimum tier that supports a given limit value */
export function getMinimumTier(
  key: TierLimitKey,
  requiredValue: number
): SubscriptionTier | null {
  for (const tier of TIER_ORDER) {
    const limit = getTierLimit(tier, key);
    if (isUnlimited(limit) || limit >= requiredValue) {
      return tier;
    }
  }
  return null;
}
